import { useQuery } from '@tanstack/react-query';
import type { Contract } from 'src/types/contract.type';
import type { ContractMilestone } from 'src/types/contractMilestone.type';

import { apiGetContract } from 'src/features/payments/api/payments.api';
import type { CashflowMonthlyRow } from '../components/CashflowChart';

function toMonth(v: string) {
    if (!v) return null;
    // ISO 날짜/시간(예: 2025-08-30Z) 또는 단순 날짜(2025-08-30)가 들어와도 UTC 기준으로 YYYY-MM만 뽑는다.
    // 이는 로컬 타임존 차이로 인해 날짜가 전월로 밀리는 현상을 방지함.
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    
    // YYYY-MM-DD 포맷의 경우 new Date()는 UTC 00:00으로 해석하므로 UTC 메서드 사용이 안전함
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
}

function monthRange(minMonth: string, maxMonth: string) {
    // minMonth/maxMonth: YYYY-MM
    const [y1, m1] = minMonth.split('-').map((x) => Number(x));
    const [y2, m2] = maxMonth.split('-').map((x) => Number(x));
    const out: string[] = [];
    let y = y1;
    let m = m1;
    for (;;) {
        out.push(`${y}-${String(m).padStart(2, '0')}`);
        if (y === y2 && m === m2) break;
        m += 1;
        if (m === 13) {
            m = 1;
            y += 1;
        }
        // 안전장치 (비정상 입력시 무한루프 방지)
        if (out.length > 240) break;
    }
    return out;
}

function emptyRow(month: string): CashflowMonthlyRow {
    return {
        month,
        planIn: 0,
        planOut: 0,
        actualIn: 0,
        actualOut: 0,
        netPlan: 0,
        netActual: 0,
        cumPlanIn: 0,
        cumPlanOut: 0,
        cumActualIn: 0,
        cumActualOut: 0,
        cumNetPlan: 0,
        cumNetActual: 0,
    };
}

function buildCashflowRows(contracts: Contract[], milestonesByContractId: Map<string, ContractMilestone[]>) {
    const monthSet = new Set<string>();
    const addMonth = (m: string | null) => {
        if (m) monthSet.add(m);
    };

    // 1) 전체 월 범위(플랜: dueDate, 실적: paidAt)
    for (const c of contracts) {
        const ms = milestonesByContractId.get(c.id) ?? [];
        for (const m of ms) {
            if (m.dueDate) addMonth(toMonth(m.dueDate));
            const pays = (m as any).payments as any[] | undefined;
            if (Array.isArray(pays)) {
                for (const p of pays) {
                    if (p?.paidAt) addMonth(toMonth(p.paidAt));
                }
            }
        }
    }

    if (monthSet.size === 0) return [] as CashflowMonthlyRow[];

    const monthsSorted = Array.from(monthSet).sort();
    const months = monthRange(monthsSorted[0], monthsSorted[monthsSorted.length - 1]);

    const map = new Map<string, CashflowMonthlyRow>();
    for (const mo of months) map.set(mo, emptyRow(mo));

    // 2) 월별 plan/actual 집계
    for (const c of contracts) {
        const isIn = c.direction === 'CLIENT';
        const ms = milestonesByContractId.get(c.id) ?? [];

        for (const m of ms) {
            if (m.dueDate) {
                const mo = toMonth(m.dueDate);
                if (mo && map.has(mo)) {
                    const row = map.get(mo)!;
                    const amt = Number(m.amountPlan) || 0;
                    if (isIn) row.planIn += amt;
                    else row.planOut += amt;
                }
            }

            const pays = (m as any).payments as any[] | undefined;
            if (Array.isArray(pays)) {
                for (const p of pays) {
                    if (!p?.paidAt) continue;
                    const mo = toMonth(p.paidAt);
                    if (mo && map.has(mo)) {
                        const row = map.get(mo)!;
                        const amt = Number(p.amount) || 0;
                        if (isIn) row.actualIn += amt;
                        else row.actualOut += amt;
                    }
                }
            }
        }
    }

    // 3) derived + cumulative
    let cumPlanIn = 0;
    let cumPlanOut = 0;
    let cumActualIn = 0;
    let cumActualOut = 0;

    const rows: CashflowMonthlyRow[] = months.map((mo) => {
        const r = map.get(mo)!;
        r.netPlan = (r.planIn || 0) - (r.planOut || 0);
        r.netActual = (r.actualIn || 0) - (r.actualOut || 0);

        cumPlanIn += r.planIn || 0;
        cumPlanOut += r.planOut || 0;
        cumActualIn += r.actualIn || 0;
        cumActualOut += r.actualOut || 0;

        r.cumPlanIn = cumPlanIn;
        r.cumPlanOut = cumPlanOut;
        r.cumActualIn = cumActualIn;
        r.cumActualOut = cumActualOut;
        r.cumNetPlan = cumPlanIn - cumPlanOut;
        r.cumNetActual = cumActualIn - cumActualOut;
        return r;
    });

    return rows;
}

export function usePaymentCashflow(projectId: string, contracts: Contract[]) {
    // queryKey에 배열 자체를 넣으면 referential identity 때문에 불필요 refetch가 생길 수 있어 문자열로 고정
    const contractIds = memoContractIds(contracts);

    return useQuery({
        queryKey: ['paymentCashflow', projectId, contractIds],
        queryFn: async () => {
            if (!contracts.length) {
                return {
                    rows: [] as CashflowMonthlyRow[],
                    currency: undefined as string | undefined,
                };
            }

            // apiListContractMilestones 대신 apiGetContract를 사용하여 
            // 마일스톤뿐만 아니라 그 아래의 payments 정보까지 확실히 확보한다.
            const fullContracts = await Promise.all(
                contracts.map(async (c) => {
                    return apiGetContract(projectId, c.id);
                }),
            );

            const milestonesByContractId = new Map<string, ContractMilestone[]>();
            for (const fc of fullContracts) {
                milestonesByContractId.set(fc.id, fc.milestones ?? []);
            }

            const rows = buildCashflowRows(fullContracts, milestonesByContractId);

            // 합의: 계약환종 기반으로 보여주되, 여러 통화 혼재 시 대표 1개만 표기 (환산은 추후)
            const currency =
                (fullContracts.find((c: Contract) => !!(c as any).currency)?.currency as any) ??
                (fullContracts.find((c: Contract) => !!(c as any).contractCurrency)?.contractCurrency as any) ??
                undefined;

            return { rows, currency };
        },
        enabled: !!projectId,
        staleTime: 30_000,
    });
}

function memoContractIds(contracts: Contract[]) {
    return (contracts || []).map((c) => c.id).sort().join('|');
}
